import { CompanyRegistryEvent } from '../types'
import {
  formatAddressCountryLabel,
  formatDate,
  formatStatusLabel,
} from './format'
import { CompanyActivityRow } from './companyActivity'

/** Toggleable activity categories (excludes `founded`). */
export type ActivityTypeToggle =
  | 'role'
  | 'name_change'
  | 'address_change'
  | 'branch_change'
  | 'form_change'
  | 'status_change'

export const ACTIVITY_TYPE_LABELS: Record<ActivityTypeToggle, string> = {
  role: 'Personer',
  name_change: 'Navn',
  address_change: 'Adresse',
  branch_change: 'Branche',
  form_change: 'Virksomhedsform',
  status_change: 'Status',
}

export const ACTIVITY_TYPE_ORDER: ActivityTypeToggle[] = [
  'role',
  'name_change',
  'address_change',
  'branch_change',
  'form_change',
  'status_change',
]

const BUCKET_SEARCH_LABEL: Record<string, string> = {
  beneficialOwners: 'reel ejer',
  ownershipRegister: 'legal ejer',
  management: 'direktion',
  boardMembers: 'bestyrelse',
  auditors: 'revision',
  other: 'anden rolle',
}

export function activityRowTypeToggle(
  row: CompanyActivityRow
): ActivityTypeToggle | null {
  if (row.source === 'role') return 'role'
  if (row.event.kind === 'founded') return null
  return row.event.kind as ActivityTypeToggle
}

export function availableActivityTypes(
  rows: CompanyActivityRow[]
): ActivityTypeToggle[] {
  const present = new Set<ActivityTypeToggle>()
  for (const row of rows) {
    const kind = activityRowTypeToggle(row)
    if (kind) present.add(kind)
  }
  return ACTIVITY_TYPE_ORDER.filter((id) => present.has(id))
}

export function activityRowSearchText(row: CompanyActivityRow): string {
  if (row.source === 'role') {
    const { participant, bucket, date } = row
    return [
      participant.navn,
      BUCKET_SEARCH_LABEL[bucket] ?? bucket,
      formatDate(date),
      participant.adresse,
    ]
      .filter(Boolean)
      .join(' ')
  }

  const { event } = row
  return [
    registryEventDescription(event),
    formatDate(event.date),
    event.newValue,
    event.previousValue,
  ]
    .filter(Boolean)
    .join(' ')
}

export function filterActivityRows(
  rows: CompanyActivityRow[],
  activeTypes: ReadonlySet<ActivityTypeToggle>,
  searchQuery = ''
): CompanyActivityRow[] {
  const q = searchQuery.trim().toLowerCase()

  return rows.filter((row) => {
    const toggle = activityRowTypeToggle(row)
    if (activeTypes.size > 0) {
      if (toggle === null || !activeTypes.has(toggle)) return false
    }
    if (!q) return true
    return activityRowSearchText(row).toLowerCase().includes(q)
  })
}

export function registryEventDescription(event: CompanyRegistryEvent): string {
  const next = event.newValue?.trim()

  switch (event.kind) {
    case 'founded':
      return next ? `Stiftet som ${next}` : 'Stiftet'
    case 'name_change':
      return next ? `Skiftede navn til ${next}` : 'Skiftede navn'
    case 'address_change':
      return next
        ? `Skiftede adresse til ${formatAddressCountryLabel(next)}`
        : 'Skiftede adresse'
    case 'form_change':
      return next ? `Skiftede virksomhedsform til ${next}` : 'Skiftede virksomhedsform'
    case 'branch_change':
      return next ? `Skiftede branche til ${next}` : 'Skiftede branche'
    case 'status_change': {
      const nextLabel = next ? formatStatusLabel(next) : null
      return nextLabel ? `Skiftede status til ${nextLabel}` : 'Skiftede status'
    }
    default:
      return 'Registreret ændring'
  }
}
